import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { InputController } from './controllers/input-controller.ts';
import { PlayerController } from './controllers/player-controller.ts';
import { SceneController } from './controllers/scene-controller.ts';
import DebugPanel from './compontent/debug-panel.ts';
import { WindowController } from './controllers/window-controller.ts';
import { PerformanceController } from './controllers/performance-controller.ts';
import { degreesToRadians } from '../../../utils/index.ts';
import { SplatConfig, InteractionZone } from '@/types';

export class MetaverseViewer {
  resetRenderState: any;
  modelAmbientLight: any;
  modelMixer: any;
  private configUrl: string;
  private container: HTMLElement;
  private clock: THREE.Clock | null;
  private animationId: number | null;
  private sceneLoaded: boolean;
  private onInteractionCallback: ((isInZone: boolean, text: string, query: string, buttonText: string) => void) | null;
  private onLoadingComplete: (() => void) | null;
  private interactionZone: THREE.Mesh[];
  private interactionBox: THREE.Box3[];
  private modelSpotLight: THREE.SpotLight[];
  private modelScene: THREE.Object3D[] | null;
  private landmark: THREE.Object3D | null;
  private landmarkRing: THREE.Object3D | null;
  private landmarkAnimTime: number;
  private lastInputTime: number;
  private isRenderingPaused: boolean;
  private renderPauseDelay: number;
  private pauseStartTime: number;
  private autoResumeDelay: number;
  private sceneController: SceneController;
  private inputController: InputController;
  private playerController: PlayerController | null;
  private windowController: WindowController;
  private performanceController: PerformanceController;
  private debugPanel: DebugPanel;
  private isInitInteractionZone: boolean;
  private config: SplatConfig = null as unknown as SplatConfig;

  constructor(
    container: HTMLElement,
    configUrl: string,
    options?: {
      onInteractionCallback?: (isInZone: boolean, text: string, query: string, buttonText: string) => void;
      onLoadingComplete?: () => void;
    },
  ) {
    console.log('container', container, configUrl);
    this.configUrl = configUrl;
    this.container = container;
    this.clock = null;
    this.animationId = null;
    this.sceneLoaded = false;
    // 创建一个回调函数，当玩家进入交互区域时调用
    this.onInteractionCallback = options?.onInteractionCallback || null;
    // Loading回调函数
    this.onLoadingComplete = options?.onLoadingComplete || null;
    this.interactionZone = [];
    this.interactionBox = [];
    this.modelSpotLight = [];
    this.modelScene = [];
    // 地标动画相关属性
    this.landmark = null;
    this.landmarkRing = null;
    this.landmarkAnimTime = 0;
    // 渲染优化相关属性
    this.lastInputTime = Date.now();
    this.isRenderingPaused = false;
    this.renderPauseDelay = 300; // 3秒后暂停渲染
    this.pauseStartTime = 0; // 记录进入不渲染模式的时间
    this.autoResumeDelay = 2000; // 2秒后自动恢复渲染
    // 初始化控制器
    this.sceneController = new SceneController(container);
    this.inputController = new InputController(container);
    this.playerController = null;
    this.windowController = new WindowController();
    this.performanceController = new PerformanceController(this.windowController.getIsMobile());
    this.debugPanel = new DebugPanel(this);
    this.isInitInteractionZone = false;
  }

  async init(): Promise<void> {
    this.config = await this.getSceneMataFile(this.configUrl);
    document.title = this.config.name || '模型';
    // 创建时钟
    this.clock = new THREE.Clock();
    // 检测设备性能（PC和移动端都需要检测）
    this.performanceController.detectDevicePerformance();
    // 等待场景初始化完成
    await this.sceneController.initScene(this.config, (percent: number, percentLabel: string, loaderStatus: number) => {
      // 复场景一大的时候人物往下掉落的问题,这里用了很取巧的解决方案，因为发现场景一大正在加载的时候，八叉树的碰撞检测就会出问题，要等场景渲染好了，再处理人物掉落
      if (loaderStatus === 2) {
        setTimeout(() => {
          this.playerController?.setSceneLoadComplete();
        }, 100);
      }
      if (percent >= 100 && !this.isInitInteractionZone && this.config?.interaction?.length) {
        // 添加交互区域
        this.addInteractionZone(this.config.interaction);
      }
    });
    // 开始帧率监测（10秒后开始）
    this.performanceController.startMonitoring();
    // 初始化玩家控制器
    this.playerController = new PlayerController(
      this.sceneController.getScene(),
      this.sceneController.getWorldOctree()!,
      this.config.avatarInfo,
    );
    await this.playerController.init();
    setTimeout(() => {
      if (this.config.avatarInfo?.camera) {
        this.sceneController.setCameraAndOribitControl(this.config.avatarInfo.camera);
      }
    }, 300);
    // 设置输入控制器的引用
    this.inputController.setCharacterControls(this.playerController);
    // 设置环绕回调函数
    this.inputController.setOrbitCallbacks(
      () => this.startOrbiting(),
      () => this.stopOrbiting(),
    );
    // 监听输入事件来重置渲染状态
    this.setupInputListeners();
    // 初始化摄像机目标
    this.sceneController.initializeCameraTarget(this.playerController.getPosition());
    this.startAnimate();
    // 加载完成后隐藏loading
    setTimeout(() => {
      if (this.onLoadingComplete) {
        this.onLoadingComplete();
      }
    }, 500);
  }

  startAnimate(): void {
    // 开始动画循环
    this.animate();
    this.animate();
  }

  async getSceneMataFile(url: string): Promise<SplatConfig> {
    const requestFile = await fetch(url, {
      mode: 'cors',
      credentials: 'omit',
      cache: 'reload',
    });
    if (requestFile.status !== 200) {
      throw new Error(`load ${url} error ${requestFile.statusText},${requestFile.body}`);
    }
    const content = await requestFile.json();
    console.log(`getSceneMataFile success file url ${url} content:`, content);
    return content;
  }
  // 设置输入监听器
  setupInputListeners(): void {
    const resetRenderState = (): void => {
      this.lastInputTime = Date.now();
      if (this.isRenderingPaused) {
        this.resetRender();
      }
    };
    // 监听键盘事件
    document.addEventListener('keydown', resetRenderState);
    document.addEventListener('keyup', resetRenderState);
    // 监听鼠标事件
    this.container.addEventListener('mousedown', resetRenderState);
    this.container.addEventListener('mousemove', resetRenderState);
    this.container.addEventListener('mouseup', resetRenderState);
    this.container.addEventListener('wheel', resetRenderState);
    // 监听触摸事件（移动端摇杆和其他触摸操作）
    // 在document上监听以确保捕获所有触摸事件，包括摇杆区域
    document.getElementById('leftArea')?.addEventListener('touchstart', resetRenderState);
    document.getElementById('leftArea')?.addEventListener('touchmove', resetRenderState);
    document.getElementById('leftArea')?.addEventListener('touchend', resetRenderState);
    document.getElementById('orbitButton')?.addEventListener('touchstart', resetRenderState);
    document.getElementById('orbitButton')?.addEventListener('touchmove', resetRenderState);
    document.getElementById('orbitButton')?.addEventListener('touchend', resetRenderState);
    document.getElementById('orbitButton')?.addEventListener('mousedown', resetRenderState);
    document.getElementById('orbitButton')?.addEventListener('mousemove', resetRenderState);
    document.getElementById('orbitButton')?.addEventListener('mouseup', resetRenderState);
    // 额外监听容器上的触摸事件作为备份
    this.container.addEventListener('touchstart', resetRenderState);
    this.container.addEventListener('touchmove', resetRenderState);
    this.container.addEventListener('touchend', resetRenderState);
    // 存储事件监听器引用以便清理
    this.resetRenderState = resetRenderState;
  }

  resetRender(): void {
    this.lastInputTime = Date.now();
    if (!this.isRenderingPaused) return;
    this.isRenderingPaused = false;
    this.performanceController.resetLowFrameRateMode();
    console.log('恢复渲染');
    this.sceneController.animate();
    setTimeout(() => {
      this.playerController?.restoreSavedPosition(this.sceneController, 'animate', true);
    }, 10);
  }

  // 添加交互区域
  addInteractionZone(options: InteractionZone[]): void {
    this.isInitInteractionZone = true;
    options.forEach((item) => {
      const geometry = new THREE.BoxGeometry(...item.geometry);
      const material = new THREE.MeshBasicMaterial({
        color: 0x00ffff,
        transparent: true,
        opacity: 0.0, // 完全透明
        visible: false, // 不可见
      });
      const zone = new THREE.Mesh(geometry, material);
      zone.position.set(...(item.geometryPos as [number, number, number]));
      this.interactionZone.push(zone);
      const box = new THREE.Box3().setFromObject(zone);
      this.interactionBox.push(box);
      this.sceneController.getScene().add(zone);
      this.addModel(
        new THREE.Vector3(
          Number(item.geometryPos[0]) + item.modelPos[0],
          Number(item.geometryPos[1]) + item.modelPos[1],
          Number(item.geometryPos[2]) + item.modelPos[2],
        ),
        item.model,
        item.scale || [1, 1, 1],
        degreesToRadians(item.rotation || [0, 0, 0]),
      );
    });
  }
  // 添加模型
  addModel(position: THREE.Vector3, name: string, scale: number[], rotationArr: number[]): void {
    const loader = new GLTFLoader();
    loader.setPath('./models/aigc/');
    // 先加载模型
    loader.load(
      name,
      (gltf) => {
        const gltfScene = gltf.scene || gltf.scenes[0];
        gltfScene.position.copy(position);
        gltfScene.rotation.y = rotationArr[1]; // 让模型面向玩家
        gltf.scene.scale.set(...(scale as [number, number, number]));
        this.modelScene!.push(gltfScene);
        // 添加到场景
        this.sceneController.getScene().add(gltfScene);
        // 创建聚光灯，只照射指定的glb模型
        const spotLight = new THREE.SpotLight(0xffffcc, 3);
        const lightPositionFront = new THREE.Vector3(
          position.x,
          position.y + 0.5, // 稍微提高光源位置
          position.z + (rotationArr[1] === 0 ? 1 : -1),
        );
        spotLight.position.copy(lightPositionFront);
        // 设置聚光灯目标为模型位置
        spotLight.target.position.copy(position);
        // 设置聚光灯参数
        spotLight.angle = Math.PI / 3; // 30度角，形成较窄的光锥
        spotLight.penumbra = 1; // 边缘柔和度
        spotLight.decay = 1; // 光线衰减
        spotLight.distance = 3; // 光照距离限制，只照射模型附近
        this.sceneController.getScene().add(spotLight);
        this.sceneController.getScene().add(spotLight.target);
        // 存储聚光灯引用
        this.modelSpotLight.push(spotLight);
      },
      undefined,
      (error) => {
        console.error('Error loading model:', error);
      },
    );
  }
  // 为labubu模型添加环境光源
  addPointLightsForLabubu(): void {
    const ambientLight = new THREE.AmbientLight(0xffffcc, 0.6);
    // 添加到场景
    this.sceneController.getScene().add(ambientLight);
    // 存储平行光引用
    this.modelAmbientLight = ambientLight;
  }

  animate(): void {
    // 监控帧率变化
    this.performanceController.monitorFrameRate();
    this.debugPanel.animate();
    this.animationId = requestAnimationFrame(this.animate.bind(this));
    // 只有在启用低帧率模式时才检查是否需要暂停渲染
    if (this.performanceController.isLowFrameRateMode()) {
      console.log('进入低帧率模式');
      const currentTime = Date.now();
      // 3s 后停止渲染
      if (currentTime - this.lastInputTime > this.renderPauseDelay) {
        if (!this.isRenderingPaused) {
          this.playerController?.saveCurrentPosition(this.sceneController, 'animate');
          this.isRenderingPaused = true;
          this.pauseStartTime = currentTime; // 记录进入不渲染模式的时间
          console.log('进入不渲染模式');
        }
        // 检查是否需要自动恢复渲染（2秒后）
        if (currentTime - this.pauseStartTime > this.autoResumeDelay) {
          this.lastInputTime = currentTime; // 重置输入时间
          console.log('2秒后自动恢复渲染');
          this.resetRenderState();
        } else {
          return; // 直接返回，不执行任何渲染操作
        }
      }
    }

    const delta = this.clock?.getDelta() || 0;
    let playerPosition: THREE.Vector3 | null = null;
    // 更新玩家
    if (this.playerController?.isLoaded()) {
      this.playerController.update(delta, this.inputController, this.sceneController.getCamera());
      // 更新场景摄像机
      playerPosition = this.playerController.getPosition();
      this.sceneController.updateCamera(playerPosition);
      // 检测玩家是否在交互区域内并更新对话框位置
      this.checkInteractionZone(playerPosition);
    }
    // 更新动画混合器
    if (this.modelMixer) {
      this.modelMixer.update(delta);
    }
    // 更新轨道控制器
    this.sceneController.getOrbitControl().update();
    // 渲染场景
    this.sceneController.animate(playerPosition!);
  }
  // 检测玩家是否在交互区域内
  checkInteractionZone(playerPosition: THREE.Vector3): void {
    if (!this.interactionZone.length || !this.interactionBox?.length) return;
    // 创建一个点来表示玩家位置
    const playerPoint = new THREE.Vector3(playerPosition.x, playerPosition.y, playerPosition.z);
    // 检查玩家是否在交互区域内
    let isInZone = false;
    let text = '';
    let query = '';
    let buttonText = '';
    this.interactionBox.forEach((box, index) => {
      if (box.containsPoint(playerPoint)) {
        text = this.config.interaction?.[index]?.dialog || '';
        query = this.config.interaction?.[index]?.query || '';
        buttonText = this.config.interaction?.[index]?.buttonText || '';
        isInZone = true;
      }
    });
    if (this.onInteractionCallback) {
      this.onInteractionCallback(isInZone, text, query, buttonText);
    }
  }
  // 兼容性方法
  switchRunToggle(): void {
    if (this.playerController) {
      this.playerController.switchRunToggle();
    }
  }

  update(delta: number, keysPressed?: Record<string, boolean>): void {
    // 兼容旧接口
    if (keysPressed) {
      this.inputController.keysPressed = keysPressed;
    }

    if (this.playerController?.isLoaded()) {
      this.playerController.update(delta, this.inputController, this.sceneController.getCamera());

      const playerPosition = this.playerController.getPosition();
      this.sceneController.updateCamera(playerPosition);
    }
  }

  destroy(): void {
    // 停止动画循环
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }
    // 清理输入事件监听器
    if (this.resetRenderState) {
      document.removeEventListener('keydown', this.resetRenderState);
      document.removeEventListener('keyup', this.resetRenderState);
      document.removeEventListener('touchstart', this.resetRenderState);
      document.removeEventListener('touchmove', this.resetRenderState);
      document.removeEventListener('touchend', this.resetRenderState);
      this.container.removeEventListener('mousedown', this.resetRenderState);
      this.container.removeEventListener('mousemove', this.resetRenderState);
      this.container.removeEventListener('mouseup', this.resetRenderState);
      this.container.removeEventListener('wheel', this.resetRenderState);
      this.container.removeEventListener('touchstart', this.resetRenderState);
      this.container.removeEventListener('touchmove', this.resetRenderState);
      this.container.removeEventListener('touchend', this.resetRenderState);
    }
    // 清理输入控制器
    this.inputController.destroy();
    // 清理模型资源
    if (this.modelScene) {
      this.sceneController.getScene().remove(this.modelScene as unknown as THREE.Object3D);
      this.modelScene = null;
    }
    // 清理动画混合器
    if (this.modelMixer) {
      this.modelMixer.stopAllAction();
      this.modelMixer = null;
    }
    // 清理灯光资源
    if (this.modelAmbientLight) {
      this.sceneController.getScene().remove(this.modelAmbientLight);
      this.modelAmbientLight.dispose();
      this.modelAmbientLight = null;
    }
    // 清理聚光灯资源
    if (this.modelSpotLight) {
      this.sceneController.getScene().remove(this.modelSpotLight as unknown as THREE.Object3D);

      // @ts-ignore
      this.sceneController.getScene().remove(this.modelSpotLight.target);
      // @ts-ignore
      this.modelSpotLight.dispose();
      // @ts-ignore

      this.modelSpotLight = null;
    }
    // 清理渲染器
    if (this.sceneController.getRenderer()) {
      this.sceneController.getRenderer()?.dispose();
    }
    // 清理窗口控制器
    if (this.windowController) {
      this.windowController.destroy();
    }
  }
  setDebugPanelVisible(visible: boolean): void {
    if (visible) {
      this.debugPanel.show();
      return;
    }
    this.debugPanel.hide();
  }
  // 开始环绕观察
  startOrbiting(): void {
    if (this.playerController?.isLoaded()) {
      const playerPosition = this.playerController.getPosition();
      this.sceneController.startOrbiting(playerPosition);
    }
  }
  // 停止环绕观察
  stopOrbiting(): void {
    this.sceneController.stopOrbiting();
  }
  // 设置交互回调函数
  setInteractionCallback(callback: (isInZone: boolean, text: string, query: string, buttonText: string) => void): void {
    this.onInteractionCallback = callback;
  }

  getIsMobile(): boolean {
    return this.windowController.getIsMobile();
  }
  // 手动进入低帧率模式
  enterLowFrameRateMode(): void {
    this.performanceController.enterLowFrameRateMode();
  }

  onWindowResize(): void {
    console.log('onWindowResize');
    this.windowController.onWindowResize();
    this.sceneController.onWindowResize();
    if (this.isRenderingPaused) this.resetRender();
  }
}
